import React from 'react';
import { Pressable, Text, View } from 'react-native';
import Unicon from '@/components/General/Unicon';
import { ActivityIndicator } from 'react-native';
import { BlurView } from 'expo-blur';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import {Tooltip} from '@/components/General/Tooltip';
import { getAccessibleTextColor } from '@/utils/colorAccessibility';

const UniBlurView = withUnistyles(BlurView);


/**
 * A modern, compact button component with subtle hover effects.
 * @param {object} props - The properties for the SolidButton component.
 * @param {string} [props.variant="normal"] - The variant of the button - one of ["normal", "outlined", "muted"]
 * @param {string} [props.appearance] - Removed; buttons are always rounded. Ignored if passed.
 * @param {string} [props.text=""] - The text for the button.
 * @param {string} [props.tooltipText=""] - Tooltip text to show on hover.
 * @param {string} [props.iconName=""] - The name of the icon to display.
 * @param {string} [props.iconLibrary="ionicons"] - The library to use for the icon.
 * @param {boolean} [props.iconBefore=false] - Whether the icon should be displayed before the text.
 * @param {Function|null} [props.onPress=null] - A callback function that is called when the button is pressed.
 * @param {string} [props.size="md"] - The size of the button ("sm", "md", "lg").
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {boolean} [props.processing=false] - Whether the button is processing (shows spinner).
 * @param {string} [props.type="primary"] - The type/color of the button ("primary", "secondary", "error", "success").
 * @param {number|null} [props.width=null] - The width of the button.
 * @param {string} [props.testID=""] - The test ID for the button.
 * @param {string} [props.accessibilityLabel=""] - Accessibility label for assistive technologies.
 * @param {string} [props.keyboardShortcutText=""] - Keyboard shortcut hint to display.
 * @returns {JSX.Element} - The rendered solid button component.
 */
const SolidButton = ({ variant = 'normal', text = "", tooltipText = "", iconName = "", iconLibrary = "ionicon", iconBefore = false, onPress = undefined, size = 'md', disabled = false, processing = false, type = "primary", width = null, testID = "", accessibilityLabel = "", keyboardShortcutText = "", customColor = null, customTextColor = null }) => {
    const onColor = 'on' + type.slice(0, 1).toUpperCase() + type.slice(1);
    const [showKeyboardShortcut, setShowKeyboardShortcut] = React.useState(false);

    const iconSize = size === "sm" ? 14 : size === 'md' ? 16 : 20;
    const isDisabled = disabled || processing;


    styles.useVariants({variant});

    const button = (
        <View style={styles.buttonWrapper}>
            <Pressable
                onHoverIn={keyboardShortcutText ? () => setShowKeyboardShortcut(true) : undefined}
                onHoverOut={keyboardShortcutText ? () => setShowKeyboardShortcut(false) : undefined}
                testID={testID || `Button-${type}-${text}`}
                accessibilityLabel={accessibilityLabel || text || tooltipText}
                accessibilityRole="button"
                disabled={isDisabled}
                onPress={onPress}
                style={styles.button(type, size, isDisabled, onColor, type, typeof onPress === "function", width, customColor)}
            >
                {({ hovered, pressed: _pressed }) => (
                    <>
                        {processing && (
                            <UniBlurView
                                intensity={90}
                                style={styles.blur()}
                            >
                                <ActivityIndicator
                                    size={size === 'lg' ? "large" : "small"}
                                />
                            </UniBlurView>
                        )}
                        {(!processing && !!iconName && iconBefore) && (
                            <Unicon name={iconName} library={iconLibrary} size={iconSize} color={customTextColor} colorVariant={variant === "normal" || (variant == "outlined" && hovered) ? onColor : type} style={styles.iconBefore} />
                        )}
                        {!!text && <Text style={styles.buttonText(isDisabled, onColor, type, variant === "outlined", size, hovered, customColor, customTextColor)}>{text}</Text>}
                        {(!processing && !!iconName && !iconBefore) && (
                            <Unicon name={iconName} library={iconLibrary} size={iconSize} color={customTextColor} colorVariant={variant === "normal" || (variant == "outlined" && hovered) ? onColor : type} style={styles.iconAfter} />
                        )}
                        {showKeyboardShortcut && (
                            <View style={styles.keyboardShortcutContainer}>
                                <Text style={styles.keyboardShortcut}>{keyboardShortcutText}</Text>
                            </View>
                        )}
                    </>
                )}
            </Pressable>
        </View>
    );

    return tooltipText ? <Tooltip title={tooltipText}>{button}</Tooltip> : button;
};


const styles = StyleSheet.create(({colors, fonts}) => ({
    iconBefore: {
        marginRight: 0
    },
    iconAfter: {
        marginLeft: 0
    },
    blur: () => ({
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        zIndex: 1,
        borderRadius: 8
    }),
    buttonWrapper: {
        _web: {
            margin: 3,
        }
    },
    button: (type, size, disabled, onColorVariant, colorVariant, canPress, width, customColor) => ({
        borderRadius: 8,
        paddingHorizontal: size === "sm" ? 11 : size === "md" ? 16 : 22,
        paddingVertical: size === "sm" ? 6 : size === "md" ? 9 : 13,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        borderWidth: 1,
        opacity: disabled ? 0.45 : 1,
        borderColor: "transparent",
        pointerEvents: canPress ? "auto" : "none",
        width: width || 'auto',
        _web: {
            transition: "all 0.2s cubic-bezier(0.34, 1.3, 0.64, 1)",
            cursor: disabled ? "not-allowed" : "pointer",
            userSelect: "none",
            // boxShadow: outline ? "none" : `0 1px 2px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.12)`,
            _hover: disabled ? {} : {
                transform: [{ scale: 1.025 }],
                // boxShadow: `0 4px 14px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.14)`,
                filter: 'brightness(1.1)',
            },
            _active: {
                transition: "all 0.08s ease",
                transform: [{ scale: 0.97 }],
                filter: 'brightness(0.9)',
            }
        },
        variants:{
            variant: {
                normal: {
                    backgroundColor: customColor || colors[colorVariant],
                },
                outlined: {
                    backgroundColor: "transparent",
                    borderColor: customColor || colors[colorVariant],
                    _web: {
                        _hover: disabled ? {} : {
                            backgroundColor: customColor || colors[colorVariant],
                            borderColor: customColor || colors[colorVariant],
                        }
                    }
                },
                muted: {
                    backgroundColor: colors[colorVariant + "Container"],
                    _web: {
                        _hover: disabled ? {} : {
                            filter: 'none',
                        }
                    }
                }
            }
        }
    }),
    buttonText: (disabled, onColorVariant, colorVariant, outlined, size, hovered, customColor, customTextColor) => ({
        textAlign: "center",
        fontFamily: fonts.bold,
        letterSpacing: size === "sm" ? 0.1 : 0.15,
        fontSize: size === "sm" ? 13 : size === "md" ? 14 : 16,
        color: customTextColor || ((outlined && !hovered) ? (customColor || colors[colorVariant]) : colors[onColorVariant] ?? getAccessibleTextColor(customColor || colors[colorVariant])),
        _web: {
            transition: "color 0.2s ease"
        },
        variants: {
            variant: {
                normal: {
                    color: customTextColor || colors[onColorVariant] || getAccessibleTextColor(customColor || colors[colorVariant]),
                },
                outlined: {
                    color: hovered
                        ? (customTextColor || colors[onColorVariant] || getAccessibleTextColor(customColor || colors[colorVariant]))
                        : (customColor || colors[colorVariant]),
                },
                muted: {
                    color: colors[colorVariant],
                }
            }
        }
    }),
    keyboardShortcutContainer: {
        position: "absolute",
        top: -17,
        right: 0
    },
    keyboardShortcut: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 10
    }
}));

export default React.memo(SolidButton);